import { Button, Card, Form, Grid, Input, Message, Select, Space } from "@arco-design/web-react"
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InputNumber } from "@/components/Numeric";
import { Currency } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import { ReferenceTypeDef } from "@/enums/purchasing";
import { SOPOAPI, SOAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import _ from "lodash";
import InventorySelect from "@/components/InventorySelect";
import Decimal from "decimal.js";
import { formatDate } from "@/utils";

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;

export const SOPickOrderEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [form] = Form.useForm();
    const [info, setInfo] = useState({});
    const [soItems, setSOItems] = useState([]);
    const [items, setItems] = useState();
    const [altItems, setAltItems] = useState([]);
    const [itemMap, setItemMap] = useState({});

    const onLotSelect = (field, lot) => {
        form.setFieldValue(`${field}.locationId`, lot.locationId);
        form.setFieldValue(`${field}.locationName`, lot.locationName);
        form.setFieldValue(`${field}.lot`, lot.lot);
        form.setFieldValue(`${field}.expiredDate`, lot.expiredDate ? formatDate(lot.expiredDate) : null);
    }

    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const values = await form.validate();
            const data = {
                id: values.id,
                soId: values.soId,
                warehouseId: values.warehouseId,
                references: values.references,
                items: _.map(values.items, (item) => _.omit(item, ['inventoryKey', 'soItemQty']))
            };
            await SOPOAPI.update(data.id, data);
            Message.success("Pick order updated successfully");
            navigate(`/workspace/sales/so-pick-order/${params.poId}`);
        } catch (error) {
            Message.error(error.message);
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        const init = async () => {
            const sopoInfo = await SOPOAPI.get(params.poId);
            const soInfo = await SOAPI.get(sopoInfo.soId);

            const map = {};
            for (const soItem of soInfo.items) {
                const itemDetail = await ItemAPI.get(soItem.itemId);
                map[itemDetail.id] = itemDetail;
            }
            setItemMap(map);

            _.forEach(sopoInfo.items, (item) => {
                item.soItemQty = _.find(soInfo.items, (soItem) => soItem.id === item.soItemId).quantity;
                const itemDetail = map[item.itemId];
                if (itemDetail.additionalUomThreshold && item.pickQty) {
                    item.pickAltQty = Decimal.div(item.pickQty, itemDetail.additionalUomThreshold);
                }
                if (item.warehouseId || item.locationId || item.lot) {
                    item.inventoryKey = {
                        warehouseId: item.warehouseId,
                        locationId: item.locationId,
                        lot: item.lot
                    };
                }
            });

            const newAltItems = sopoInfo.items.map(item => {
                const itemDetail = map[item.itemId];
                return itemDetail?.additionalUom;
            });
            setAltItems(newAltItems);

            setInfo(sopoInfo);

            let sortedItems = _.sortBy(soInfo.items, "itemName");
            setItems(_.map(sortedItems, (item) => ({
                label: <span>{item.itemName}(<Currency value={item.rate} />)</span>,
                value: item.id
            })));
            setSOItems(soInfo.items);

            form.setFieldsValue(sopoInfo);
        };
        init();
    }, [params, form])

    return (
        <Card title="Edit Pick Order" extra={
            <Space>
                <Button type="primary" loading={saving} onClick={() => save()}>Save</Button>
                <Button onClick={() => navigate(`/workspace/sales/so-pick-order/${params.poId}`)}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}>
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>
                <FormItem field="soId" hidden>
                    <Input />
                </FormItem>
                <FormItem field="warehouseId" hidden>
                    <Input />
                </FormItem>
                <FormItem>
                    <Row gutter={8}>
                        <Col span={4}>
                            <FormItem label="SO#" field="soNo" labelCol={{ span: 6 }}>
                                <Input readOnly />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem label="Customer Name" field="customerName" labelCol={{ span: 12 }}>
                                <Input readOnly />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem label="SO Due Date" field="soDueDate" labelCol={{ span: 12 }}>
                                <Input readOnly />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Warehouse" field="warehouseName" labelCol={{ span: 10 }}>
                                <Input readOnly />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={12}>
                            <FormItem label="Reference">
                                <Form.List field="references">
                                    {(fields, { add, remove }) =>
                                        <>
                                            {fields.map((item, index) => {
                                                return (
                                                    <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                        <Space className="form-item-row">
                                                            <FormItem field={item.field + ".type"}>
                                                                <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} showSearch />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".number"}>
                                                                <Input />
                                                            </FormItem>
                                                            <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                        </Space>
                                                    </FormItem>
                                                )
                                            })}
                                            <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                        </>
                                    }
                                </Form.List>
                            </FormItem>
                        </Col>
                    </Row>
                </FormItem>
                <FormItem label="Items" labelCol={{ span: 1 }}>
                    <Form.List field="items">
                        {(fields, { add, remove }) =>
                            <>
                                <table width="100%">
                                    <thead>
                                        <tr>
                                            <th>Line#</th>
                                            <th width="80">Item Number</th>
                                            <th>Item Name</th>
                                            <th>Description</th>
                                            <th width="120">SO Qty</th>
                                            <th width="120">Pick Qty</th>
                                            <th width="60">UOM</th>
                                            <th width="120">Alt Qty</th>
                                            <th width="300">Location|Lot#|Expiration</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fields.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <span>#{index + 1}</span>
                                                        <FormItem field={item.field + ".id"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".itemId"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".itemNo"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".soItemId"} noStyle>
                                                            <Select options={items}
                                                                showSearch
                                                                filterOption={(inputValue, option) => option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0}
                                                                triggerProps={{
                                                                    autoAlignPopupWidth: false,
                                                                    autoAlignPopupMinWidth: true
                                                                }}
                                                                onChange={(val) => {
                                                                    const soItem = _.find(soItems, o => o.id === val);
                                                                    form.setFieldValue(`${item.field}.itemId`, soItem.itemId);
                                                                    form.setFieldValue(`${item.field}.itemNo`, soItem.itemNo);
                                                                    form.setFieldValue(`${item.field}.description`, soItem.description);
                                                                    form.setFieldValue(`${item.field}.soItemQty`, soItem.quantity);
                                                                    form.setFieldValue(`${item.field}.uom`, soItem.uom);
                                                                    form.setFieldValue(`${item.field}.pickQty`, null);
                                                                    form.setFieldValue(`${item.field}.pickAltQty`, null);
                                                                    form.setFieldValue(`${item.field}.locationId`, null);
                                                                    form.setFieldValue(`${item.field}.locationName`, null);
                                                                    form.setFieldValue(`${item.field}.lot`, null);
                                                                    form.setFieldValue(`${item.field}.inventoryKey`, null);
                                                                    form.setFieldValue(`${item.field}.expiredDate`, null);
                                                                    altItems[index] = itemMap[soItem.itemId]?.additionalUom;
                                                                    setAltItems([...altItems]);
                                                                }} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".description"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".soItemQty"} noStyle>
                                                            <InputNumber readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".pickQty"} required rules={[{ required: true }]} noStyle>
                                                            <InputNumber onChange={(val) => {
                                                                if (!val) {
                                                                    form.setFieldValue(`${item.field}.pickAltQty`, null);
                                                                }
                                                                const itemDetail = itemMap[form.getFieldValue(`${item.field}.itemId`)];
                                                                if (itemDetail?.additionalUomThreshold) {
                                                                    const pickAltQty = Decimal.div(val, itemDetail.additionalUomThreshold);
                                                                    form.setFieldValue(`${item.field}.pickAltQty`, pickAltQty);
                                                                }
                                                            }} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".uom"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".pickAltQty"} noStyle>
                                                            <InputNumber readOnly suffix={altItems[index]} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".locationId"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".locationName"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".lot"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".expiredDate"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".inventoryKey"} noStyle>
                                                            <InventorySelect
                                                                warehouseId={info.warehouseId}
                                                                getItemId={() => {
                                                                    return form.getFieldValue(`${item.field}.itemId`);
                                                                }}
                                                                getPickQty={() => {
                                                                    return form.getFieldValue(`${item.field}.pickQty`);
                                                                }}
                                                                onSelect={(val) => onLotSelect(item.field, val)} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem noStyle>
                                                            <Button type="text" status="danger" icon={<IconDelete />}
                                                                onClick={() => remove(index)}></Button>
                                                        </FormItem>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                                <FormItem>
                                    <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Item</Button>
                                </FormItem>
                            </>
                        }
                    </Form.List>
                </FormItem>
            </Form>
        </Card>
    )
}
